import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { Counsellor } from '../../types/counsellor';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import DocumentStatusManagement from '../shared/DocumentStatusManagement';
import DbsManagement from '../Counsellor/DbsManagement';

dayjs.extend(relativeTime);

interface LegalInfoManagementProps {
  counsellorId: string;
  counsellorData: Counsellor;
  onRefresh: () => void;
}

interface ValidationChecks {
  identityVerified: boolean;
  dbsVerified: boolean;
  nationalityVerified: boolean;
  workPermitVerified: boolean;
  insuranceVerified: boolean;
  convictionsVerified: boolean;
}

// Chip colour by per-credential severity: expired/missing block (red),
// expiring/check_required are advisories (amber), compliant is green.
const complianceSeverityColor = (status: string): 'success' | 'warning' | 'error' => {
  if (status === 'compliant') return 'success';
  if (status === 'expired' || status === 'missing') return 'error';
  return 'warning';
};

interface UpdateChecks {
  identityNeedsUpdate: boolean;
  dbsNeedsUpdate: boolean;
  nationalityNeedsUpdate: boolean;
  workPermitNeedsUpdate: boolean;
  insuranceNeedsUpdate: boolean;
  convictionsNeedsUpdate: boolean;
  onlineLearningCertificateNeedsUpdate: boolean;
}

const LegalInfoManagement: React.FC<LegalInfoManagementProps> = ({
  counsellorId,
  counsellorData,
  onRefresh
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [requestUpdateDialogOpen, setRequestUpdateDialogOpen] = useState(false);
  const [validateLegalInfoDialogOpen, setValidateLegalInfoDialogOpen] = useState(false);
  const [dbsTransitionDialogOpen, setDbsTransitionDialogOpen] = useState(false);
  const [editDbsUpdateServiceDialogOpen, setEditDbsUpdateServiceDialogOpen] = useState(false);
  
  // Form states
  const [adminNotes, setAdminNotes] = useState('');
  const [dbsTransitionNotes, setDbsTransitionNotes] = useState('');
  const [dbsUpdateServiceId, setDbsUpdateServiceId] = useState('');
  const [dbsNumber, setDbsNumber] = useState('');
  const [legalFirstName, setLegalFirstName] = useState('');
  const [legalLastName, setLegalLastName] = useState('');
  const [dbsUpdateServiceConsent, setDbsUpdateServiceConsent] = useState(false);
  const [validationChecks, setValidationChecks] = useState<ValidationChecks>({
    identityVerified: false,
    dbsVerified: false,
    nationalityVerified: false,
    workPermitVerified: false,
    insuranceVerified: false,
    convictionsVerified: false
  });
  
  const [updateChecks, setUpdateChecks] = useState<UpdateChecks>({
    identityNeedsUpdate: false,
    dbsNeedsUpdate: false,
    nationalityNeedsUpdate: false,
    workPermitNeedsUpdate: false,
    insuranceNeedsUpdate: false,
    convictionsNeedsUpdate: false,
    onlineLearningCertificateNeedsUpdate: false
  });
  
  // Get token for API calls
  const [token, setToken] = useState<string>('');

  useEffect(() => {
    // Initialize validation checks from counsellor data if available
    if (counsellorData?.legalInfoValidationChecks) {
      setValidationChecks(counsellorData.legalInfoValidationChecks);
    }
    
    // Get token from localStorage for API calls
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    }
  }, [counsellorData]);

  const handleRequestUpdate = async () => {
    try {
      if (!adminNotes.trim()) {
        setError('Admin notes are required');
        return;
      }
      
      // Check if at least one update checkbox is selected
      const hasSelectedUpdates = Object.values(updateChecks).some(value => value === true);
      if (!hasSelectedUpdates) {
        setError('Please select at least one item that needs updating');
        return;
      }
      
      setLoading(true);
      setError(null);
      
      await api.post(`/api/counsellors/${counsellorId}/request-legal-update`, {
        adminNotes,
        updateChecks
      });
      
      setSuccess('Update request sent successfully!');
      setRequestUpdateDialogOpen(false);
      setAdminNotes('');
      // Reset update checks
      setUpdateChecks({
        identityNeedsUpdate: false,
        dbsNeedsUpdate: false,
        nationalityNeedsUpdate: false,
        workPermitNeedsUpdate: false,
        insuranceNeedsUpdate: false,
        convictionsNeedsUpdate: false,
        onlineLearningCertificateNeedsUpdate: false
      });
      onRefresh();
    } catch (error: any) {
      console.error('Error requesting update:', error);
      setError(error.response?.data?.error || 'Failed to request legal information update');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateLegalInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if Trust ID is verified before allowing validation
      if (!counsellorData.trustIdVerification?.verified) {
        setError('Trust ID verification must be completed before validating legal information. Please verify the counsellor\'s identity using Trust ID first.');
        setLoading(false);
        return;
      }
      
      // Check if all validation checks are confirmed
      const allChecksConfirmed = Object.values(validationChecks).every(check => check === true);
      if (!allChecksConfirmed) {
        setError('All validation checks must be confirmed before approving legal information.');
        setLoading(false);
        return;
      }
      
      await api.post(`/api/counsellors/${counsellorId}/validate-legal-info`, {
        adminNotes,
        validationChecks,
        approved: true
      });
      
      setSuccess('Legal information validated successfully!');
      setValidateLegalInfoDialogOpen(false);
      setAdminNotes('');
      onRefresh();
    } catch (error: any) {
      console.error('Error validating legal info:', error);
      setError(error.response?.data?.error || 'Failed to validate legal information');
    } finally {
      setLoading(false);
    }
  };

  const handleDbsTransition = async () => {
    try {
      if (!dbsUpdateServiceId.trim()) {
        setError('DBS Update Service ID is required');
        return;
      }
      
      if (!dbsNumber.trim()) {
        setError('DBS Number is required');
        return;
      }
      
      if (!dbsUpdateServiceConsent) {
        setError('Counsellor consent for DBS Update Service checks is required');
        return;
      }
      
      if (!dbsTransitionNotes.trim()) {
        setError('Admin notes are required for this transition');
        return;
      }
      
      setLoading(true);
      setError(null);
      
      await api.post(`/api/counsellors/${counsellorId}/transition-dbs-to-update-service`, {
        dbsUpdateServiceId,
        dbsNumber,
        legalFirstName: legalFirstName.trim() || undefined,
        legalLastName: legalLastName.trim() || undefined,
        dbsUpdateServiceConsent,
        adminNotes: dbsTransitionNotes
      });
      
      setSuccess('DBS successfully transitioned to Update Service!');
      setDbsTransitionDialogOpen(false);
      setDbsTransitionNotes('');
      setDbsUpdateServiceId('');
      setDbsNumber('');
      setLegalFirstName('');
      setLegalLastName('');
      setDbsUpdateServiceConsent(false);
      onRefresh();
    } catch (error: any) {
      console.error('Error transitioning DBS:', error);
      setError(error.response?.data?.error || 'Failed to transition DBS to Update Service');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDbsUpdateService = async () => {
    try {
      if (!dbsUpdateServiceId.trim()) {
        setError('DBS Update Service ID is required');
        return;
      }
      
      if (!dbsNumber.trim()) {
        setError('DBS Number is required');
        return;
      }
      
      if (!dbsUpdateServiceConsent) {
        setError('Counsellor consent for DBS Update Service checks is required');
        return;
      }
      
      if (!dbsTransitionNotes.trim()) {
        setError('Admin notes are required for this update');
        return;
      }
      
      setLoading(true);
      setError(null);
      
      // Use dedicated update endpoint for existing DBS Update Service records
      await api.post(`/api/counsellors/${counsellorId}/update-dbs-update-service`, {
        dbsUpdateServiceId,
        dbsNumber,
        legalFirstName: legalFirstName.trim() || undefined,
        legalLastName: legalLastName.trim() || undefined,
        dbsUpdateServiceConsent,
        adminNotes: dbsTransitionNotes
      });
      
      setSuccess('DBS Update Service details updated successfully!');
      setEditDbsUpdateServiceDialogOpen(false);
      setDbsTransitionNotes('');
      setDbsUpdateServiceId('');
      setDbsNumber('');
      setLegalFirstName('');
      setLegalLastName('');
      setDbsUpdateServiceConsent(false);
      onRefresh();
    } catch (error: any) {
      console.error('Error updating DBS Update Service details:', error);
      setError(error.response?.data?.error || 'Failed to update DBS Update Service details');
    } finally {
      setLoading(false);
    }
  };

  // Get legal information status chip based on counsellor's legal information status
  const getLegalStatusChip = (counsellorData: Counsellor) => {
    if (counsellorData.legalInfoValidated) {
      return <Chip label="Validated" color="success" />;
    } else if (counsellorData.legalFormSubmitted) {
      return <Chip label="Submitted" color="warning" />;
    } else if (counsellorData.legalFormToken) {
      return <Chip label="Form Sent" color="info" />;
    } else {
      return <Chip label="Not Started" color="default" />;
    }
  };

  const renderActionButtons = () => {
    const status = counsellorData.applicationStatus;
    
    // Don't show validation buttons for existing counsellors who are already "Ready For Onboarding"
    // These counsellors were added through the new onboarding flow and validated via Trust ID
    if (status === 'Ready For Onboarding' && counsellorData.legalInfoValidated) {
      return null;
    }
    
    return (
      <div className="flex gap-4">
        {(status === 'Legal Info Submitted' || status === 'Legal Info Update Requested' || (status === 'Onboarding Review' && counsellorData.legalFormSubmitted)) && (
          <>
            <button
              onClick={() => setValidateLegalInfoDialogOpen(true)}
              className="px-4 py-2 text-sm font-bold text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 min-w-[160px]"
            >
              Validate & Approve
            </button>
            
            <button
              onClick={() => setRequestUpdateDialogOpen(true)}
              className="px-4 py-2 text-sm font-bold text-white bg-orange-600 border border-transparent rounded-md shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 min-w-[160px]"
            >
              Request Updates
            </button>
          </>
        )}
      </div>
    );
  };

  const renderLegalInfoStatus = () => {
    return (
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className="glass-card-light p-6">
              <div className="flex justify-between items-center mb-6">
                <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Legal Information Status
                </Typography>
                {renderActionButtons()}
              </div>
              {!counsellorData.legalFormSubmitted ? getLegalStatusChip(counsellorData) : null}
              {counsellorData.legalFormSubmitted ? (
                <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>Form Submitted:</strong> {dayjs(counsellorData.legalFormSubmittedAt).format('MMM D, YYYY [at] h:mm A')} ({dayjs(counsellorData.legalFormSubmittedAt).fromNow()})
                </Typography>
              ) : null}
              
              <Grid container spacing={2}>
                  {counsellorData.legalFormToken ? (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Legal Form Link:</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {`${window.location.origin}/legal-form/${counsellorData.legalFormToken}`}
                      </Typography>
                    </Grid>
                  ) : null}
                  
                  {counsellorData.legalFormSentAt ? (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Form Sent:</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {dayjs(counsellorData.legalFormSentAt).format('MMM D, YYYY [at] h:mm A')}
                        {' '}
                        ({dayjs(counsellorData.legalFormSentAt).fromNow()})
                      </Typography>
                    </Grid>
                  ) : null}
                  
                  {counsellorData.legalFormExpiryDate ? (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Form Expires:</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ color: new Date(counsellorData.legalFormExpiryDate) < new Date() ? 'error.main' : 'rgba(255, 255, 255, 0.7)' }}
                      >
                        {dayjs(counsellorData.legalFormExpiryDate).format('MMM D, YYYY [at] h:mm A')}
                        {' '}
                        ({dayjs(counsellorData.legalFormExpiryDate).fromNow()})
                      </Typography>
                    </Grid>
                  ) : null}
                  
                  {counsellorData.legalInfoValidated && counsellorData.legalInfoValidatedAt ? (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Validated On:</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {dayjs(counsellorData.legalInfoValidatedAt).format('MMM D, YYYY [at] h:mm A')}
                        {' '}
                        ({dayjs(counsellorData.legalInfoValidatedAt).fromNow()})
                      </Typography>
                    </Grid>
                  ) : null}
                  
                  {(counsellorData.legalInfoAdminNotes || counsellorData.legalInfoUpdateAreas?.length) ? (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Update Request Details:</Typography>
                      <div className="glass-card p-4">
                        {counsellorData.legalInfoUpdateAreas?.length ? (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight="bold" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                              Areas requiring updates:
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                              {counsellorData.legalInfoUpdateAreas.map((area, index) => (
                                <li key={index}>
                                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{area}</Typography>
                                </li>
                              ))}
                            </ul>
                          </Box>
                        ) : null}
                        {counsellorData.legalInfoAdminNotes ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                              Admin Notes:
                            </Typography>
                            <Typography variant="body2" whiteSpace="pre-wrap" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {counsellorData.legalInfoAdminNotes}
                            </Typography>
                          </Box>
                        ) : null}
                      </div>
                    </Grid>
                  ) : null}
              </Grid>
              
              {/* Only show validation checks if the counsellor has been accepted after interview */}
              {counsellorData.legalInfoValidationChecks && (counsellorData.applicationStatus === 'Accepted' || counsellorData.applicationStatus === 'Onboarding Review') && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Validation Checks:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {counsellorData.legalInfoValidationChecks.identityVerified ? 
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} /> : 
                          <CancelIcon color="error" sx={{ mr: 1 }} />
                        }
                        <Typography>Identity Verified</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {counsellorData.legalInfoValidationChecks.dbsVerified ? 
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} /> : 
                          <CancelIcon color="error" sx={{ mr: 1 }} />
                        }
                        <Typography>DBS Verified</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {counsellorData.legalInfoValidationChecks.nationalityVerified ? 
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} /> : 
                          <CancelIcon color="error" sx={{ mr: 1 }} />
                        }
                        <Typography>Nationality Verified</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {counsellorData.legalInfoValidationChecks.workPermitVerified ? 
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} /> : 
                          <CancelIcon color="error" sx={{ mr: 1 }} />
                        }
                        <Typography>Work Permit Verified</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {counsellorData.legalInfoValidationChecks.insuranceVerified ? 
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} /> : 
                          <CancelIcon color="error" sx={{ mr: 1 }} />
                        }
                        <Typography>Insurance Verified</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {counsellorData.legalInfoValidationChecks.convictionsVerified ? 
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} /> : 
                          <CancelIcon color="error" sx={{ mr: 1 }} />
                        }
                        <Typography>Convictions Verified</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </div>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render the submitted legal information
  const renderSubmittedLegalInfo = () => {
    // Only show if legal info has been submitted
    if (!counsellorData?.application?.legal || !counsellorData.legalFormSubmitted) {
      return null;
    }

    const legalInfo = counsellorData.application.legal;

    return (
      <Box sx={{ mt: 4, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          Submitted Legal Information
        </Typography>
        <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        <Grid container spacing={3}>
          {/* DBS Check */}
          <Grid item xs={12} md={6}>
            <div className="glass-card-light p-4">
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>DBS Check</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Has DBS: {legalInfo.dbsCheck ? 'Yes' : 'No'}
              </Typography>
              {legalInfo.dbsCheck && (
                <>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    DBS Number: {legalInfo.dbsNumber || 'Not provided'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    DBS Issue Date: {legalInfo.dbsIssueDate ? dayjs(legalInfo.dbsIssueDate).format('DD/MM/YYYY') : 'Not provided'}
                  </Typography>
                  {legalInfo.dbsExpiryDate && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      DBS Expiry Date: {dayjs(legalInfo.dbsExpiryDate).format('DD/MM/YYYY')} (calculated)
                    </Typography>
                  )}
                  {legalInfo.dbsUpdateServiceId && (
                    <>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
                        DBS Update Service ID: {legalInfo.dbsUpdateServiceId}
                      </Typography>
                      {(legalInfo.legalFirstName || legalInfo.legalLastName) && (
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Legal Name on DBS: {legalInfo.legalFirstName || ''} {legalInfo.legalLastName || ''}
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </Grid>

          {/* Insurance */}
          <Grid item xs={12} md={6}>
            <div className="glass-card-light p-4">
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Insurance</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Insurance Provider: {legalInfo.insuranceProvider || 'Not provided'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Insurance Number: {legalInfo.insuranceNumber || 'Not provided'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Insurance Expiry Date: {legalInfo.insuranceExpiryDate ? dayjs(legalInfo.insuranceExpiryDate).format('DD/MM/YYYY') : 'Not provided'}
              </Typography>
              {legalInfo.insuranceCertificate && (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Insurance Certificate: {legalInfo.insuranceCertificate.name || 'Uploaded'}
                </Typography>
              )}
            </div>
          </Grid>

          {/* Work Permit */}
          <Grid item xs={12} md={6}>
            <div className="glass-card-light p-4">
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Work Permit</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Work Permit Required: {legalInfo.workPermit ? 'Yes' : 'No'}
              </Typography>
              {legalInfo.workPermit && (
                <>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Work Permit Details: {legalInfo.workPermitDetails || 'Not provided'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Work Permit Expiry: {legalInfo.workPermitNoExpiry ? 'No expiry date (indefinite)' : legalInfo.workPermitExpiryDate ? dayjs(legalInfo.workPermitExpiryDate).format('DD/MM/YYYY') : 'Not provided'}
                  </Typography>
                </>
              )}
            </div>
          </Grid>

          {/* Convictions */}
          <Grid item xs={12} md={6}>
            <div className="glass-card-light p-4">
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Convictions</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Has Convictions: {legalInfo.convictions ? 'Yes' : 'No'}
              </Typography>
              {legalInfo.convictions && (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Conviction Details: {legalInfo.convictionsDetails || 'Not provided'}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Under Criminal Investigation: {legalInfo.criminalInvestigation ? 'Yes' : 'No'}
              </Typography>
              {legalInfo.criminalInvestigation && (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Investigation Details: {legalInfo.criminalInvestigationDetails || 'Not provided'}
                </Typography>
              )}
            </div>
          </Grid>

          {/* Identification */}
          <Grid item xs={12} md={6}>
            <div className="glass-card-light p-4">
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Identification</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Nationality: {legalInfo.nationality || 'Not provided'}
              </Typography>
            </div>
          </Grid>

          {/* Professional Details */}
          <Grid item xs={12} md={6}>
            <div className="glass-card-light p-4">
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Professional Details</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Supervisor Name: {legalInfo.supervisorName || 'Not provided'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Supervisor Phone: {legalInfo.supervisorPhoneNumber || 'Not provided'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                BACP / NCPS Number: {legalInfo.bacpNumber || 'Not provided'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Online Learning Course: {legalInfo.onlineLearningCourse ? 'Yes' : 'No'}
              </Typography>
              {legalInfo.onlineLearningCourse && (
                <>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Online Learning Details: {legalInfo.onlineLearningDetails || 'Not provided'}
                  </Typography>
                  {legalInfo.onlineLearningCertificate && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Online Learning Certificate: {legalInfo.onlineLearningCertificate.fileName || legalInfo.onlineLearningCertificate.fileUrl || 'Uploaded'}
                    </Typography>
                  )}
                </>
              )}
            </div>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <div className="glass-card p-6 mt-3">
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      {/* Compliance Status Alert */}
      {counsellorData.complianceStatus && (
        <Box sx={{ mb: 3 }}>
          {counsellorData.complianceStatus.overall === 'non_compliant' && (
            <Alert 
              severity="error" 
              icon={<ErrorIcon />}
              sx={{ 
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                '& .MuiAlert-icon': { color: '#ef4444' },
                '& .MuiAlert-message': { color: 'rgba(255, 255, 255, 0.9)' }
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Non-Compliant Status Detected
              </Typography>
              <Typography variant="body2">
                This counsellor is currently non-compliant. Please review the issues below:
              </Typography>
              {counsellorData.complianceStatus.issues && counsellorData.complianceStatus.issues.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {counsellorData.complianceStatus.issues.map((issue: string, index: number) => (
                    <li key={index}>
                      <Typography variant="body2">{issue}</Typography>
                    </li>
                  ))}
                </Box>
              )}
              {counsellorData.complianceStatus.warnings && counsellorData.complianceStatus.warnings.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {counsellorData.complianceStatus.warnings.map((warning: string, index: number) => (
                    <li key={index}>
                      <Typography variant="body2" sx={{ color: '#fbbf24' }}>{warning}</Typography>
                    </li>
                  ))}
                </Box>
              )}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {counsellorData.complianceStatus.dbs && (
                  <Chip 
                    label={`DBS: ${counsellorData.complianceStatus.dbs}`}
                    size="small"
                    color={complianceSeverityColor(counsellorData.complianceStatus.dbs)}
                  />
                )}
                {counsellorData.complianceStatus.insurance && (
                  <Chip 
                    label={`Insurance: ${counsellorData.complianceStatus.insurance}`}
                    size="small"
                    color={complianceSeverityColor(counsellorData.complianceStatus.insurance)}
                  />
                )}
                {counsellorData.complianceStatus.workPermit && counsellorData.complianceStatus.workPermit !== 'not_required' && (
                  <Chip 
                    label={`Work Permit: ${counsellorData.complianceStatus.workPermit}`}
                    size="small"
                    color={complianceSeverityColor(counsellorData.complianceStatus.workPermit)}
                  />
                )}
              </Box>
            </Alert>
          )}
          {counsellorData.complianceStatus.overall === 'action_required' && (
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ 
                bgcolor: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                '& .MuiAlert-icon': { color: '#fbbf24' },
                '& .MuiAlert-message': { color: 'rgba(255, 255, 255, 0.9)' }
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Action Required
              </Typography>
              <Typography variant="body2">
                This counsellor requires attention for compliance:
              </Typography>
              {(counsellorData.complianceStatus.warnings ?? counsellorData.complianceStatus.issues)?.map((warning: string, index: number) => (
                <Box component="ul" key={index} sx={{ mt: index === 0 ? 1 : 0, mb: 0, pl: 2 }}>
                  <li>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                </Box>
              ))}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {counsellorData.complianceStatus.dbs && (
                  <Chip 
                    label={`DBS: ${counsellorData.complianceStatus.dbs}`}
                    size="small"
                    color={complianceSeverityColor(counsellorData.complianceStatus.dbs)}
                  />
                )}
                {counsellorData.complianceStatus.insurance && (
                  <Chip 
                    label={`Insurance: ${counsellorData.complianceStatus.insurance}`}
                    size="small"
                    color={complianceSeverityColor(counsellorData.complianceStatus.insurance)}
                  />
                )}
                {counsellorData.complianceStatus.workPermit && counsellorData.complianceStatus.workPermit !== 'not_required' && (
                  <Chip 
                    label={`Work Permit: ${counsellorData.complianceStatus.workPermit}`}
                    size="small"
                    color={complianceSeverityColor(counsellorData.complianceStatus.workPermit)}
                  />
                )}
              </Box>
            </Alert>
          )}
          {counsellorData.complianceStatus.overall === 'compliant' && (
            <Alert 
              severity="success" 
              icon={<CheckCircleIcon />}
              sx={{ 
                bgcolor: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                '& .MuiAlert-icon': { color: '#22c55e' },
                '& .MuiAlert-message': { color: 'rgba(255, 255, 255, 0.9)' }
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Compliant
              </Typography>
              <Typography variant="body2">
                All compliance checks passed. DBS and Insurance are up to date.
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <Chip label="DBS: Compliant" size="small" color="success" />
                <Chip label="Insurance: Compliant" size="small" color="success" />
              </Box>
            </Alert>
          )}
        </Box>
      )}
      
      {renderLegalInfoStatus()}
      {renderSubmittedLegalInfo()}

      {/* Document Confirmations */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <DocumentStatusManagement 
            counsellorId={counsellorId}
            onSuccess={(message: string) => setSuccess(message)}
            onError={(message: string) => setError(message)}
          />
        </Grid>
      </Grid>
      
      {/* DBS Management Section - Grouped */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          DBS Management
        </Typography>
        <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        {/* DBS Certificate - Managed via Credentials */}
        <Box sx={{ mb: 3, p: 3, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
              DBS Certificate
            </Typography>
            {counsellorData.application?.legal?.dbsType === 'certificate' && (
              <Chip 
                label="Certificate Type" 
                size="small" 
                sx={{ 
                  bgcolor: 'rgba(59, 130, 246, 0.2)', 
                  color: '#60a5fa',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }} 
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2 }}>
            DBS certificates are managed through the Credential Management system on the counsellor's profile page.
            This includes certificate uploads, expiry tracking, and renewal workflows.
          </Typography>
          
          {/* Transition to Update Service Button */}
          {counsellorData.application?.legal?.dbsType === 'certificate' && counsellorData.application?.legal?.dbsCheck && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDbsTransitionDialogOpen(true)}
              sx={{
                mt: 1,
                color: 'rgba(255, 255, 255, 0.9)',
                borderColor: 'rgba(99, 102, 241, 0.5)',
                '&:hover': {
                  borderColor: 'rgba(99, 102, 241, 0.8)',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)'
                }
              }}
            >
              Transition to Update Service
            </Button>
          )}
          
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic', display: 'block', mt: 2 }}>
            Note: Navigate to the counsellor's profile and access the Credentials section to manage DBS certificates.
          </Typography>
        </Box>
        
        {/* DBS Update Service */}
        {token && counsellorData.application?.legal && (counsellorData.application.legal as any).dbsUpdateServiceConsent && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                DBS Update Service
              </Typography>
              {counsellorData.application?.legal?.dbsType === 'update_service' && (
                <Chip 
                  label="Update Service" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(34, 197, 94, 0.2)', 
                    color: '#4ade80',
                    border: '1px solid rgba(34, 197, 94, 0.3)'
                  }} 
                />
              )}
            </Box>
            <DbsManagement
              counsellorId={counsellorId}
              isAdmin={true}
              refreshCounsellor={onRefresh}
              token={token}
            />
            
            {/* Edit DBS Update Service Details Button */}
            {counsellorData.application?.legal?.dbsType === 'update_service' && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  // Pre-populate form with existing data
                  setDbsUpdateServiceId(counsellorData.application?.legal?.dbsUpdateServiceId || '');
                  setDbsNumber(counsellorData.application?.legal?.dbsNumber || '');
                  setLegalFirstName((counsellorData as any).legalFirstName || '');
                  setLegalLastName((counsellorData as any).legalLastName || '');
                  setDbsUpdateServiceConsent((counsellorData as any).dbsUpdateServiceConsent || false);
                  setEditDbsUpdateServiceDialogOpen(true);
                }}
                sx={{
                  mt: 2,
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(99, 102, 241, 0.5)',
                  '&:hover': {
                    borderColor: 'rgba(99, 102, 241, 0.8)',
                    backgroundColor: 'rgba(99, 102, 241, 0.1)'
                  }
                }}
              >
                Edit DBS Update Service Details
              </Button>
            )}
          </Box>
        )}
      </Box>
      
      {/* Insurance & Other Credentials */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          Insurance & Other Credentials
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2 }}>
          Insurance certificates, work permits, and other credentials are managed through the Credential Management system.
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
          Note: Navigate to the counsellor's profile and access the Credentials section to manage all certificates and credentials.
        </Typography>
      </Box>
      
      {/* Request Update Dialog */}
      <Dialog
        open={requestUpdateDialogOpen}
        onClose={() => setRequestUpdateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>Request Legal Information Update</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
            Please select the items that need to be updated and provide details about what information needs to be corrected.
            This will be included in the email sent to the counsellor.
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Items Requiring Updates:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateChecks.dbsNeedsUpdate}
                      onChange={(e) => setUpdateChecks({
                        ...updateChecks,
                        dbsNeedsUpdate: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="DBS Certificate"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateChecks.nationalityNeedsUpdate}
                      onChange={(e) => setUpdateChecks({
                        ...updateChecks,
                        nationalityNeedsUpdate: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Nationality Information"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateChecks.workPermitNeedsUpdate}
                      onChange={(e) => setUpdateChecks({
                        ...updateChecks,
                        workPermitNeedsUpdate: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Right to Work Information"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateChecks.insuranceNeedsUpdate}
                      onChange={(e) => setUpdateChecks({
                        ...updateChecks,
                        insuranceNeedsUpdate: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Insurance Certificate"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateChecks.onlineLearningCertificateNeedsUpdate}
                      onChange={(e) => setUpdateChecks({
                        ...updateChecks,
                        onlineLearningCertificateNeedsUpdate: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Telephone Certificate"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateChecks.convictionsNeedsUpdate}
                      onChange={(e) => setUpdateChecks({
                        ...updateChecks,
                        convictionsNeedsUpdate: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Convictions Information"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
            </Grid>
          </Box>
          
          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <TextField
            autoFocus
            label="Admin Notes"
            fullWidth
            multiline
            rows={4}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            margin="normal"
            required
            error={!adminNotes.trim() && requestUpdateDialogOpen}
            helperText={!adminNotes.trim() && requestUpdateDialogOpen ? 'Admin notes are required' : 'Please provide specific details about what needs to be updated'}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
              '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestUpdateDialogOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleRequestUpdate}
            variant="contained"
            color="primary"
            disabled={loading || !adminNotes.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Request Update'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Validate Legal Info Dialog */}
      <Dialog
        open={validateLegalInfoDialogOpen}
        onClose={() => setValidateLegalInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>Validate Legal Information</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
            Please verify that you have reviewed all legal information and documents provided by the counsellor.
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Validation Checklist:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={validationChecks.identityVerified}
                      onChange={(e) => setValidationChecks({
                        ...validationChecks,
                        identityVerified: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Identity Verified"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={validationChecks.dbsVerified}
                      onChange={(e) => setValidationChecks({
                        ...validationChecks,
                        dbsVerified: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="DBS Verified"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={validationChecks.nationalityVerified}
                      onChange={(e) => setValidationChecks({
                        ...validationChecks,
                        nationalityVerified: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Nationality Verified"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={validationChecks.workPermitVerified}
                      onChange={(e) => setValidationChecks({
                        ...validationChecks,
                        workPermitVerified: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Work Permit Verified"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={validationChecks.insuranceVerified}
                      onChange={(e) => setValidationChecks({
                        ...validationChecks,
                        insuranceVerified: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Insurance Verified"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={validationChecks.convictionsVerified}
                      onChange={(e) => setValidationChecks({
                        ...validationChecks,
                        convictionsVerified: e.target.checked
                      })}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  }
                  label="Convictions Verified"
                  sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.8)' } }}
                />
              </Grid>
            </Grid>
          </Box>
          
          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <TextField
            label="Admin Notes (Optional)"
            fullWidth
            multiline
            rows={4}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            margin="normal"
            helperText="Add any notes about the validation process (optional)"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
              '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidateLegalInfoDialogOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleValidateLegalInfo}
            variant="contained"
            color="success"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Validate & Approve'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* DBS Transition Dialog */}
      <Dialog
        open={dbsTransitionDialogOpen}
        onClose={() => setDbsTransitionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
          Transition DBS to Update Service
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
            <Typography variant="body2">
              This will transition the counsellor from a DBS Certificate to the DBS Update Service.
              The counsellor must have already registered with the DBS Update Service and provided their Update Service ID.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Please enter the counsellor's DBS Update Service details to enable automatic online status checks.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="DBS Number"
                value={dbsNumber}
                onChange={(e) => setDbsNumber(e.target.value)}
                required
                helperText="The DBS certificate number"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="DBS Update Service ID"
                value={dbsUpdateServiceId}
                onChange={(e) => setDbsUpdateServiceId(e.target.value)}
                required
                helperText="The 12-digit Update Service ID"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                If the name on the DBS certificate differs from the counsellor's preferred name, please enter their legal name:
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Legal First Name (Optional)"
                value={legalFirstName}
                onChange={(e) => setLegalFirstName(e.target.value)}
                helperText="Only if different from preferred name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Legal Last Name (Optional)"
                value={legalLastName}
                onChange={(e) => setLegalLastName(e.target.value)}
                helperText="Only if different from preferred name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dbsUpdateServiceConsent}
                    onChange={(e) => setDbsUpdateServiceConsent(e.target.checked)}
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      '&.Mui-checked': { color: '#6366f1' } 
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    I confirm that the counsellor has consented to their DBS Update Service record being checked online
                  </Typography>
                }
              />
              {!dbsUpdateServiceConsent && (
                <Typography variant="caption" sx={{ color: '#f87171', display: 'block', mt: 1, ml: 4 }}>
                  Counsellor consent is required to enable DBS Update Service checks
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin Notes"
                multiline
                rows={4}
                value={dbsTransitionNotes}
                onChange={(e) => setDbsTransitionNotes(e.target.value)}
                required
                helperText="Please document the reason for this transition and any verification performed"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDbsTransitionDialogOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDbsTransition}
            variant="contained"
            disabled={loading || !dbsUpdateServiceId.trim() || !dbsNumber.trim() || !dbsTransitionNotes.trim() || !dbsUpdateServiceConsent}
            sx={{
              backgroundColor: '#6366f1',
              '&:hover': { backgroundColor: '#4f46e5' },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Transition to Update Service'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit DBS Update Service Dialog */}
      <Dialog
        open={editDbsUpdateServiceDialogOpen}
        onClose={() => setEditDbsUpdateServiceDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
          Edit DBS Update Service Details
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
            <Typography variant="body2">
              Update the counsellor's DBS Update Service details. This is useful when correcting errors or updating information that has changed.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Please enter the updated DBS Update Service details:
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="DBS Number"
                value={dbsNumber}
                onChange={(e) => setDbsNumber(e.target.value)}
                required
                helperText="Enter the DBS certificate number"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="DBS Update Service ID"
                value={dbsUpdateServiceId}
                onChange={(e) => setDbsUpdateServiceId(e.target.value)}
                required
                helperText="Enter the DBS Update Service ID"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                If the name on the DBS certificate differs from the counsellor's preferred name, enter their legal name:
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Legal First Name (Optional)"
                value={legalFirstName}
                onChange={(e) => setLegalFirstName(e.target.value)}
                helperText="Only if different from preferred name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Legal Last Name (Optional)"
                value={legalLastName}
                onChange={(e) => setLegalLastName(e.target.value)}
                helperText="Only if different from preferred name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dbsUpdateServiceConsent}
                    onChange={(e) => setDbsUpdateServiceConsent(e.target.checked)}
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      '&.Mui-checked': { color: '#6366f1' } 
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    I confirm that the counsellor has consented to their DBS Update Service record being checked online
                  </Typography>
                }
              />
              {!dbsUpdateServiceConsent && (
                <Typography variant="caption" sx={{ color: '#f87171', display: 'block', mt: 1, ml: 4 }}>
                  Counsellor consent is required to enable DBS Update Service checks
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin Notes"
                multiline
                rows={4}
                value={dbsTransitionNotes}
                onChange={(e) => setDbsTransitionNotes(e.target.value)}
                required
                helperText="Please document the reason for this update and any verification performed"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDbsUpdateServiceDialogOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditDbsUpdateService}
            variant="contained"
            disabled={loading || !dbsUpdateServiceId.trim() || !dbsNumber.trim() || !dbsTransitionNotes.trim() || !dbsUpdateServiceConsent}
            sx={{
              backgroundColor: '#6366f1',
              '&:hover': { backgroundColor: '#4f46e5' },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Update Details'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LegalInfoManagement;
